import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { ExportToExcelService } from './export-to-excel.service';
import { ExportConfig, ExportInputData, FileType, FormType, StackingPlanExportTypes } from './export.model';
import { FloorSizeStrategy } from './new-export-to-excel.service';
import { LocaleId } from '../../../models/locale.models';
import { EXPORT_FORM_CONFIG } from './export.config';
import { DataObject } from '../../../models/data-object.models';

@Component({
  selector: 'sp-export',
  templateUrl: './export.component.html',
  styleUrls: ['./export.component.scss'],
  providers: [ExportToExcelService],
  standalone: false,
})
export class ExportComponent implements OnInit, OnDestroy {
  private readonly localeId = LocaleId.US;
  readonly formConfig = EXPORT_FORM_CONFIG;
  readonly formType = FormType;
  exportForm: UntypedFormGroup;

  get showOptions(): DataObject {
    return this.exportData.exportSettings?.displayOptions || {};
  }

  get showParameters(): DataObject {
    return this.exportData.exportSettings?.cardDetails || {};
  }

  get fileType(): FileType {
    return this.exportData.exportSettings?.fileType || FileType.Excel;
  }

  get sizeStrategy(): FloorSizeStrategy {
    return FloorSizeStrategy.Building;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private exportData: ExportInputData,
    private dialogRef: MatDialogRef<ExportComponent>
  ) {}

  ngOnInit(): void {
    this.exportForm = this.formInit(this.formConfig);
  }

  ngOnDestroy(): void {
    // required by angular-l10n
  }

  #populateData(formValue: DataObject): DataObject {
    const { showParameters } = formValue;
    if (showParameters?.rentDate) {
      showParameters.includeEstimatedRateIndicator = this.showParameters?.includeEstimatedRateIndicator;
      showParameters.includeEstimatedRates = this.showParameters?.includeEstimatedRates;
    }
    showParameters.leaseRateOpts = this.showParameters?.leaseRateOpts;
    return { ...formValue, showParameters, exportType: StackingPlanExportTypes.SPExcel };
  }

  exportReport(): void {
    const exportDialogValue = this.#populateData(this.exportForm.value);
    this.dialogRef.close(exportDialogValue);
  }

  private formInit(config: ExportConfig[], value?: any, validators?: ValidatorFn[]): UntypedFormGroup {
    return new UntypedFormGroup(this.buildFormGroup(config, value), validators);
  }

  // * Builds form group out of config information
  private buildFormGroup(
    config: ExportConfig[],
    value?: any
  ): { [key: string]: UntypedFormControl | UntypedFormGroup } {
    return config.reduce((controls, config) => {
      const { key } = config;
      const _value = value ? value[key] : (this[key] ?? false);
      return {
        ...controls,
        [key]: this.getControlByType(_value, config),
      };
    }, {});
  }

  private getControlByType(
    value: any,
    { key, formType, options, validators }: ExportConfig
  ): UntypedFormControl | UntypedFormGroup {
    return formType === FormType.GROUP && options.length
      ? this.formInit(options, this[key], validators)
      : new UntypedFormControl(value, validators);
  }
}
